(ns ui.button
  (:require
   ["react" :as react]
   [applied-science.js-interop :as jsi]
   [clojure.set :as c.set]
   [lucide-react :refer [Loader2]]
   [reagent.core :as reagent]
   [ui.utils :refer [clsx separate-props]]))

(defn- button-tag
  [{:keys [href component]}]
  (cond
    component component
    href      :a
    :else     :button))

(defn button-class
  [{:keys [class variant size full-width disabled rounded]}]
  (let [variant (or (some-> variant keyword) :primary)
        size (some-> size keyword)]
    {:class (clsx "relative inline-flex items-center justify-center font-medium rounded-lg transition-colors text-kleene-dark-01 leading-5 focus:ring-3 focus:outline-hidden text-regular active:scale-[0.98] cursor-pointer"
                  (when disabled
                    "pointer-events-none opacity-75 grayscale cursor-not-allowed")
                  (case variant
                    :primary   "bg-kleene-primary-01 hover:bg-kleene-primary-02 active:bg-kleene-primary-01 focus:ring-kleene-primary-04/30 dark:text-kleene-text-darkest"
                    :secondary
                    (clsx "border border-kleene-neutral-01 bg-white dark:bg-kleene-dark-02 hover:bg-kleene-neutral-05 dark:hover:bg-kleene-dark-03 focus:ring-kleene-neutral-04/30 dark:border-kleene-dark-04 dark:text-kleene-neutral-01 "
                          {"bg-kleene-neutral-05 text-kleene-text-secondary border-kleene-neutral-01/50 dark:bg-kleene-dark-03 dark:text-kleene-neutral-04 dark:border-kleene-dark-04/50" disabled})
                    :empty     (clsx "bg-kleene-neutral-05 hover:bg-kleene-neutral-04 focus:ring-kleene-neutral-04/30 text-kleene-dark-01 radix-state-active:bg-kleene-neutral-04"
                                     "dark:bg-kleene-dark-02 dark:hover:bg-kleene-dark-03 dark:text-kleene-neutral-01")
                    :empty-transparent (clsx "bg-transparent hover:bg-kleene-neutral-04/50 focus:ring-kleene-neutral-04/30 text-kleene-dark-01 radix-state-active:bg-kleene-neutral-04/50"
                                             "dark:bg-kleene-dark-02/50 dark:hover:bg-kleene-dark-03/50 dark:text-kleene-neutral-01 radix-state-active:bg-kleene-dark-02/50")
                    :error        "bg-kleene-error-02 hover:bg-kleene-error-03 active:bg-kleene-error-02 focus:ring-kleene-error-04/30"
                    :error-border (clsx "border border-kleene-error-02 text-kleene-error-01 hover:bg-kleene-error-04 active:bg-kleene-error-05 focus:ring-kleene-error-04/30")
                    :error-empty (clsx "bg-kleene-text-error/10 hover:bg-kleene-text-error/20 focus:ring-kleene-neutral-04/30 text-kleene-text-error radix-state-active:bg-kleene-neutral-04 text-center "
                                       "dark:bg-kleene-dark-02 dark:hover:bg-kleene-dark-03 dark:text-kleene-neutral-01")
                    :warning   "bg-kleene-warning-02  hover:bg-kleene-warning-03 active:bg-kleene-warning-02 focus:ring-kleene-warning-04/30 text-white"
                    :warning-border (clsx "border border-kleene-warning-02 text-kleene-warning-01 hover:bg-kleene-warning-04 active:bg-kleene-warning-05 focus:ring-kleene-warning-04/30")
                    :destructive (clsx "bg-kleene-error-02 hover:bg-kleene-error-03 active:bg-kleene-error-02 focus:ring-kleene-error-04/30 text-white")
                    :primary-border (clsx "border border-kleene-primary-v2 text-kleene-primary-v2 hover:bg-kleene-primary-04 active:bg-kleene-neutral-05 focus:ring-kleene-primary-04/30 disabled:border-kleene-primary-v2/50")
                    :link      (clsx "hover:bg-kleene-neutral-04 focus:ring-kleene-neutral-04/30 text-kleene-green border-transparent bg-transparent"
                                     "dark:hover:bg-kleene-dark-03 dark:text-kleene-green")
                    (js/console.error (str "Unkown button variant:" variant)))
                  (case size
                    :micro   "px-1.5 py-0.5 text-xs"
                    :small   "px-3 py-1.5"
                    :large   "px-6 py-3 text-lg"
                    :icon-xs "p-1"
                    :icon-sm "p-1"
                    :icon    "p-1.5"
                    :icon-md "p-2"
                    :icon-sl "p-2.5"
                    :icon-lg "p-3"
                    "px-4 py-2")
                  {"w-full"       full-width
                   "rounded-full" rounded}
                  class)}))

(defn- content-class
  [{:keys [loading size content-class]}]
  (clsx "flex gap-2 items-center justify-center w-full"
        content-class
        {"opacity-0" loading}
        (case size
          :micro "gap-0.5"
          "")))

(defn- button-loader []
  [:div {:class "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6"}
   [:> Loader2 {:class "animate-spin"
                :size  24}]])

(defn button
  [& args]
  (let [[props children] (separate-props args)
        {:keys [loading icon icon-props icon-right-props size icon-size icon-right]} props
        tag        (button-tag props)
        class      (button-class props)
        full-props (merge
                    {:type :button}
                    (dissoc props :variant :size :loading :icon :icon-props :icon-size :icon-right-props :icon-right :full-width :component :rounded :wrapper-class)
                    class)
        icon-size  (or icon-size
                       (case size
                         :large 20
                         :small 14
                         :icon-xs 12
                         :icon-md 18
                         :micro 12
                         16))]
    [tag full-props
     (when loading [button-loader])

     [:div {:class (content-class props)}
      (when icon [:span {:class "leading-zero"}
                  [:> icon (merge {:size icon-size} icon-props)]])
      (when (seq children)
        (into [:div {:class (clsx "flex-1 text-left overflow-hidden text-ellipsis"
                                  {"-my-px" (= (:variant props) :secondary)}
                                  (:wrapper-class props))}]
              children))
      (when icon-right [:span {:class "leading-zero"}
                        [:> icon-right (merge {:size icon-size} icon-right-props)]])]]))

(def button-fr
  (react/forwardRef
   (fn [props ref]
     (reagent/as-element [button
                          (-> (js->clj props :keywordize-keys true)
                              (assoc :ref ref)
                              (dissoc :children)
                              (c.set/rename-keys {:wrapperClass :wrapper-class
                                                  :className    :class}))
                          (jsi/get props :children)]))))
